<script setup lang="ts">
/**
 * Page DÉTAIL d'un dossier d'onboarding / offboarding (V2).
 *
 * Montée sur `/onboarding/:id`. Consomme le store `useOnboardingStore` (API
 * boostcore `/material`) : en-tête dossier, avancement, détails collaborateur et
 * inventaire regroupé par catégorie (rendu via `OnbAssetCard`). La fiche de remise
 * est générée localement (signature non encore persistée) ; « modifier le dossier »
 * reste un placeholder (info inline « Bientôt disponible »).
 * Ton visuel aligné sur `ExportsCegidAdminPage.vue` (cards, badges, alerts).
 */
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ArrowLeftIcon,
  ArrowRightOnRectangleIcon,
  BoltIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PencilSquareIcon,
  SparklesIcon,
  TrashIcon,
  UserPlusIcon,
} from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import {
  CASE_SOURCE_META,
  CASE_STATUS_META,
  CASE_TYPE_META,
  CATEGORY_META,
  computeProgress,
  progressCounts,
} from '@/v2/onboarding/catalog'
import type { AssetAssignment, AssetCategory } from '@/v2/onboarding/types'
import OnbProgressBar from '@/v2/components/onboarding/OnbProgressBar.vue'
import OnbAssetCard from '@/v2/components/onboarding/OnbAssetCard.vue'
import OnbHandoverDialog from '@/v2/components/onboarding/OnbHandoverDialog.vue'

const route = useRoute()
const router = useRouter()
const store = useOnboardingStore()

const id = route.params.id as string
const c = computed(() => store.getCase(id))

// Chargement initial des données (API) — une seule fois au montage.
onMounted(() => store.ensureLoaded())

function goToList() {
  router.push('/onboarding')
}

// ── Détails collaborateur ────────────────────────────────────────────────────
function formatDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR')
}

const collabFields = computed(() => {
  const cc = c.value
  if (!cc) return []
  const col = cc.collaborator
  return [
    { label: 'Poste', value: col.jobTitle || '—' },
    { label: 'Entité', value: col.entity || '—' },
    { label: 'Manager (N+1)', value: col.manager || '—' },
    { label: 'Agence', value: col.agency || '—' },
    { label: 'Email', value: col.email || '—' },
    { label: 'Mobile', value: col.mobile || '—', numeric: true },
    { label: '3CX', value: col.phone3cx || '—', numeric: true },
    { label: "Date d'arrivée", value: formatDate(cc.arrivalDate), numeric: true },
    { label: 'Date de sortie', value: formatDate(cc.exitDate), numeric: true },
  ]
})

// ── Inventaire regroupé par catégorie (objets actifs) ────────────────────────
const CATEGORY_ORDER: AssetCategory[] = ['IT', 'TELECOM', 'VEHICLE', 'ACCESS', 'OTHER']

const activeGroups = computed(() => {
  const cc = c.value
  if (!cc) return []
  const active = cc.assets.filter((a) => a.status !== 'REPLACED')
  return CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_META[category].label,
    assets: active.filter((a) => a.category === category),
  })).filter((g) => g.assets.length > 0)
})

/** Objets remplacés (inactifs) — affichés à part s'il y en a. */
const replacedAssets = computed<AssetAssignment[]>(
  () => c.value?.assets.filter((a) => a.status === 'REPLACED') ?? [],
)

const hasAssets = computed(() => (c.value?.assets.length ?? 0) > 0)

const caption = computed(() => {
  const cc = c.value
  if (!cc) return ''
  const { done, total } = progressCounts(cc)
  return `${done}/${total} objets ${cc.type === 'OFFBOARDING' ? 'restitués' : 'remis'}`
})

// ── Actions sans endpoint dédié (placeholder « bientôt » + confirmation fiche) ─
const actionNotice = ref('')

function notifySoon(action: string) {
  actionNotice.value = `${action} — bientôt disponible.`
}

// ── Fiche de remise (FORM1) — modale signable ────────────────────────────────
const handoverRef = ref<InstanceType<typeof OnbHandoverDialog> | null>(null)

function openHandover() {
  handoverRef.value?.open()
}

function onHandoverSaved() {
  actionNotice.value = 'Fiche de remise signée et enregistrée (P10-SI-GP-FORM1-V1).'
}

// ── Suppression de la demande ─────────────────────────────────────────────────
const confirmingDelete = ref(false)
const deleting = ref(false)

async function handleDelete() {
  deleting.value = true
  try {
    await store.deleteCase(id)
    router.push('/onboarding')
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <!-- ── Dossier introuvable ───────────────────────────────────────────── -->
    <section v-if="!c" class="card border border-base-300 bg-base-100 shadow-sm" aria-labelledby="notfound-title">
      <div class="card-body items-center gap-3 py-12 text-center">
        <ExclamationTriangleIcon class="h-12 w-12 text-base-content/50" aria-hidden="true" />
        <h1 id="notfound-title" class="text-xl font-semibold">Dossier introuvable</h1>
        <p class="max-w-prose text-base-content/80">
          Le dossier demandé n'existe pas ou a été supprimé.
        </p>
        <button type="button" class="btn btn-primary" @click="goToList">
          <ArrowLeftIcon class="h-5 w-5" aria-hidden="true" />
          Retour à la liste
        </button>
      </div>
    </section>

    <template v-else>
      <!-- ── Fil d'Ariane ────────────────────────────────────────────────── -->
      <nav aria-label="Fil d'Ariane" class="text-sm">
        <ol role="list" class="flex flex-wrap items-center gap-1 text-base-content/70">
          <li><RouterLink to="/accueil" class="link link-hover">Accueil</RouterLink></li>
          <li aria-hidden="true"><ChevronRightIcon class="h-4 w-4" /></li>
          <li>Hub Interne</li>
          <li aria-hidden="true"><ChevronRightIcon class="h-4 w-4" /></li>
          <li><RouterLink to="/onboarding" class="link link-hover">Matériel collaborateur</RouterLink></li>
          <li aria-hidden="true"><ChevronRightIcon class="h-4 w-4" /></li>
          <li class="font-medium text-base-content tabular-nums" aria-current="page">{{ c.reference }}</li>
        </ol>
      </nav>

      <!-- ── En-tête dossier ─────────────────────────────────────────────── -->
      <section class="card mt-4 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="case-title">
        <div class="card-body gap-4 p-4 md:p-6">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div class="min-w-0">
              <h1 id="case-title" class="text-2xl font-bold">
                {{ c.collaborator.firstName }} {{ c.collaborator.lastName }}
              </h1>

              <div class="mt-2 flex flex-wrap items-center gap-2">
                <!-- Type -->
                <span
                  class="badge gap-1"
                  :class="c.type === 'OFFBOARDING' ? 'badge-warning' : 'badge-success'"
                >
                  <ArrowRightOnRectangleIcon v-if="c.type === 'OFFBOARDING'" class="h-4 w-4" aria-hidden="true" />
                  <UserPlusIcon v-else class="h-4 w-4" aria-hidden="true" />
                  {{ CASE_TYPE_META[c.type].label }}
                </span>
                <!-- Statut -->
                <span class="badge" :class="CASE_STATUS_META[c.status].badgeClass">
                  {{ CASE_STATUS_META[c.status].label }}
                </span>
                <!-- Source -->
                <span class="badge badge-ghost gap-1">
                  <BoltIcon v-if="c.source === 'LUCCA'" class="h-4 w-4" aria-hidden="true" />
                  {{ CASE_SOURCE_META[c.source].label }}
                </span>
                <!-- Fiche de remise signée -->
                <span v-if="c.handovers && c.handovers.length" class="badge badge-success gap-1">
                  <DocumentTextIcon class="h-4 w-4" aria-hidden="true" />
                  Fiche signée
                </span>
                <!-- Référence -->
                <span class="text-sm tabular-nums text-base-content/80">{{ c.reference }}</span>
              </div>
            </div>

            <!-- Actions : fiche de remise (locale) + placeholders « bientôt » -->
            <div class="flex w-full flex-wrap gap-2 sm:w-auto">
              <button type="button" class="btn btn-primary w-full sm:w-auto" @click="openHandover">
                <DocumentTextIcon class="h-5 w-5" aria-hidden="true" />
                Générer la fiche de remise
              </button>
              <RouterLink
                :to="`/onboarding/collaborateurs/${encodeURIComponent(c.collaborator.userUuid ?? '')}`"
                class="btn btn-ghost w-full sm:w-auto"
              >
                <UserPlusIcon class="h-5 w-5" aria-hidden="true" />
                Fiche collaborateur
              </RouterLink>
              <button type="button" class="btn btn-ghost w-full sm:w-auto" @click="notifySoon('Modification du dossier')">
                <PencilSquareIcon class="h-5 w-5" aria-hidden="true" />
                Modifier
              </button>
              <button
                type="button"
                class="btn btn-ghost w-full text-error sm:w-auto"
                @click="confirmingDelete = true"
              >
                <TrashIcon class="h-5 w-5" aria-hidden="true" />
                Supprimer
              </button>
            </div>
          </div>

          <div v-if="actionNotice" role="status" class="alert alert-info">
            <InformationCircleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{{ actionNotice }}</span>
          </div>

          <!-- Confirmation de suppression -->
          <div v-if="confirmingDelete" role="alertdialog" aria-label="Confirmer la suppression" class="alert alert-warning flex-wrap">
            <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
            <span class="flex-1">
              Supprimer définitivement cette demande&nbsp;? Le matériel déjà détenu par le
              collaborateur est conservé (seuls les objets prévus non remis sont retirés).
            </span>
            <div class="flex flex-wrap gap-2">
              <button type="button" class="btn btn-ghost" :disabled="deleting" @click="confirmingDelete = false">
                Annuler
              </button>
              <button type="button" class="btn btn-error" :disabled="deleting" :aria-busy="deleting" @click="handleDelete">
                <TrashIcon class="h-5 w-5" aria-hidden="true" />
                Confirmer la suppression
              </button>
            </div>
          </div>

          <!-- ── Avancement ──────────────────────────────────────────────── -->
          <OnbProgressBar :value="computeProgress(c)" :caption="caption" />
        </div>
      </section>

      <!-- ── Détails collaborateur ───────────────────────────────────────── -->
      <section class="card mt-6 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="collab-title">
        <div class="card-body gap-4 p-4 md:p-6">
          <h2 id="collab-title" class="card-title text-lg font-semibold">Détails collaborateur</h2>
          <dl class="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            <div v-for="f in collabFields" :key="f.label">
              <dt class="text-sm text-base-content/70">{{ f.label }}</dt>
              <dd class="text-base break-words text-base-content" :class="{ 'tabular-nums': f.numeric }">
                {{ f.value }}
              </dd>
            </div>
          </dl>

          <div v-if="c.comment" class="rounded-box border border-base-300 bg-base-200/40 p-3">
            <h3 class="mb-1 text-sm text-base-content/70">Commentaire</h3>
            <p class="text-base whitespace-pre-line text-base-content/80">{{ c.comment }}</p>
          </div>
        </div>
      </section>

      <!-- ── Inventaire & équipements ────────────────────────────────────── -->
      <section class="mt-6" aria-labelledby="inventory-title">
        <h2 id="inventory-title" class="text-lg font-semibold">Inventaire &amp; équipements</h2>

        <!-- État vide -->
        <div
          v-if="!hasAssets"
          class="card mt-3 border border-base-300 bg-base-100 shadow-sm"
        >
          <div class="card-body items-center gap-3 py-10 text-center">
            <SparklesIcon class="h-10 w-10 text-base-content/50" aria-hidden="true" />
            <p class="text-base-content/80">Aucun équipement renseigné — dossier en brouillon.</p>
            <button type="button" class="btn btn-ghost" @click="notifySoon('Application d\'une dotation par défaut')">
              Appliquer une dotation par défaut
            </button>
          </div>
        </div>

        <template v-else>
          <!-- Objets actifs regroupés par catégorie -->
          <div
            v-for="group in activeGroups"
            :key="group.category"
            class="mt-5"
          >
            <h3 class="mb-3 flex items-center gap-2 font-semibold text-base-content/80">
              {{ group.label }}
              <span class="badge badge-sm badge-ghost tabular-nums">{{ group.assets.length }}</span>
            </h3>
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <OnbAssetCard
                v-for="asset in group.assets"
                :key="asset.id"
                :asset="asset"
                :case-id="c.id"
                :case-type="c.type"
              />
            </div>
          </div>

          <!-- Objets remplacés (historique) -->
          <div v-if="replacedAssets.length" class="mt-8">
            <h3 class="mb-3 font-semibold text-base-content/70">Historique / objets remplacés</h3>
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <OnbAssetCard
                v-for="asset in replacedAssets"
                :key="asset.id"
                :asset="asset"
                :case-id="c.id"
                :case-type="c.type"
              />
            </div>
          </div>
        </template>
      </section>

      <!-- ── Fiche de remise (FORM1) — modale signable ───────────────────── -->
      <OnbHandoverDialog ref="handoverRef" :case-data="c" @saved="onHandoverSaved" />
    </template>
  </div>
</template>
